import { useRoute } from "vue-router"
import { useCurrentContextStore } from "@/store/currentContextStore"
import { useAssessmentStore } from "@/store/assessmentStore"
import { SupportRequestClient } from "../../entities/NSwagGenerated"

export interface SupportContext {
	pageUrl: string
	pageLabel: string
	/** Human-friendly label of the section the user was in (from route.meta.supportLocation). */
	location: string
	/** Identifier for the kind of page (from route.meta.captureScope). Drives which fields are relevant. */
	scope: string
	projectId: string | null
	subProjectId: string | null
	assessmentId: string | null
	projectName: string | null
	subProjectName: string | null
	assessmentName: string | null
	tabName: string | null
	subTabName: string | null
	userAgent: string
}

export function useSupportContext() {
	// useRoute() and stores must be called during setup, not inside callbacks
	const route = useRoute()
	const currentContextStore = useCurrentContextStore()
	const assessmentStore = useAssessmentStore()

	function pickId(source: Record<string, unknown>, keys: string[]): string | null {
		for (const key of keys) {
			const v = source[key]
			if (typeof v === "string" && v.length > 0) return v
		}
		return null
	}

	function captureContext(): SupportContext {
		const params = route.params as Record<string, unknown>
		const query = route.query as Record<string, unknown>
		const meta = (route.meta ?? {}) as { supportLocation?: string; captureScope?: string }
		const scope = meta.captureScope ?? "other"
		const isAssessment = scope === "assessment"

		// IDs may live in route params or in query string (e.g. ?assessmentId=...)
		const projectId = pickId(params, ["projectId"]) ?? pickId(query, ["projectId"])

		const subProjectId =
			pickId(params, ["subProjectId", "subprojectId"]) ??
			pickId(query, ["subProjectId", "subprojectId"])

		// The assessmentStore can only be trusted when the user is actually inside the
		// assessment editor. Outside of it the store value is stale from a previous session.
		const assessmentId =
			pickId(params, ["assessmentId", "id"]) ??
			pickId(query, ["assessmentId", "id"]) ??
			(isAssessment ? assessmentStore.getCurrentAssessment ?? null : null)

		return {
			pageUrl: route.fullPath,
			pageLabel: route.name?.toString() ?? route.path,
			location: meta.supportLocation ?? route.name?.toString() ?? route.path,
			scope,
			projectId,
			subProjectId,
			assessmentId,
			projectName: null,
			subProjectName: null,
			assessmentName: null,
			// tab/subTab only meaningful inside the assessment editor.
			tabName: isAssessment ? currentContextStore.mainTabName : null,
			subTabName: isAssessment ? currentContextStore.subTabName : null,
			userAgent: navigator.userAgent,
		}
	}

	async function openSupportPopup(): Promise<void> {
		const context = captureContext()

		// Resolve project / subProject / assessment names here (in the parent window) so the
		// popup needs ZERO HTTP calls during its mount. The submit POST stays the only request.
		if (context.projectId || context.subProjectId || context.assessmentId) {
			try {
				const client = new SupportRequestClient(import.meta.env.VITE_API_URL?.toString())
				const result = await client.resolveContext(
					context.projectId ?? undefined,
					context.subProjectId ?? undefined,
					context.assessmentId ?? undefined,
				)
				if (result) {
					context.projectId = context.projectId ?? result.projectId ?? null
					context.subProjectId = context.subProjectId ?? result.subProjectId ?? null
					context.assessmentId = context.assessmentId ?? result.assessmentId ?? null
					context.projectName = result.projectName ?? null
					context.subProjectName = result.subProjectName ?? null
					context.assessmentName = result.assessmentName ?? null
				}
			} catch {
				// Non-fatal: the popup still opens and lets the user submit without resolved names
			}
		}

		localStorage.setItem("supportContext", JSON.stringify(context))
		// Clean up any leftover screenshot keys from previous attempts (legacy auto-capture)
		localStorage.removeItem("supportScreenshot")
		localStorage.removeItem("supportScreenshotStatus")
		localStorage.removeItem("supportScreenshotError")

		window.open(
			"/support-request.html",
			"supportRequest",
			"width=720,height=900,resizable=yes,scrollbars=yes",
		)
	}

	return { captureContext, openSupportPopup }
}
